import { CaptainLogo } from "./CaptainLogo";
import { Anchor, Heart } from "lucide-react";

interface FooterProps {
  t: (en: string, fr: string, es?: string) => string;
}

const footerLinks = {
  training: [
    { href: "/crr-zone1", labelEn: "CRR Zone 1 — VHF", labelFr: "CRR Zone 1 — VHF", labelEs: "CRR Zona 1 — VHF" },
    { href: "/crr-zone2", labelEn: "CRR Zone 2 — MF", labelFr: "CRR Zone 2 — MF", labelEs: "CRR Zona 2 — MF" },
    { href: "/crr-zone3", labelEn: "CRR Zone 3 — HF/Sat", labelFr: "CRR Zone 3 — HF/Sat", labelEs: "CRR Zona 3 — HF/Sat" },
    { href: "/crr-flash", labelEn: "CRR Flash Cards", labelFr: "Cartes Flash CRR", labelEs: "Tarjetas Flash CRR" },
  ],
  explore: [
    { href: "/maritime-careers", labelEn: "Maritime Careers", labelFr: "Carrières maritimes", labelEs: "Carreras marítimas" },
    { href: "/scuba-diving", labelEn: "Scuba Diving", labelFr: "Plongée", labelEs: "Buceo" },
    { href: "/about", labelEn: "About", labelFr: "À propos", labelEs: "Acerca de" },
    { href: "/contact", labelEn: "Contact", labelFr: "Contact", labelEs: "Contacto" },
  ],
};

export const Footer = ({ t }: FooterProps) => {
  return (
    <footer className="border-t border-border/30 bg-card/40">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {/* Brand column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <CaptainLogo size={28} />
              <span className="font-bold text-foreground tracking-wide">Captain ROCA</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              {t(
                "AI Companion for Maritime Career Guidance — Built for Dyslexic Minds.",
                "Compagnon IA pour l'orientation maritime — Conçu pour les esprits dyslexiques.",
                "Compañero IA para orientación marítima — Diseñado para mentes disléxicas."
              )}
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Heart className="w-3.5 h-3.5 text-destructive" />
              <span>{t("Free & open to everyone", "Gratuit & ouvert à tous", "Gratuito y abierto a todos")}</span>
            </div>
          </div>

          {/* Training links */}
          <div>
            <h4 className="font-semibold text-foreground text-sm mb-4 tracking-wide uppercase">
              {t("Training", "Formation", "Formación")}
            </h4>
            <ul className="space-y-2.5">
              {footerLinks.training.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-accent transition-colors no-underline"
                  >
                    {t(link.labelEn, link.labelFr, link.labelEs)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Explore links */}
          <div>
            <h4 className="font-semibold text-foreground text-sm mb-4 tracking-wide uppercase">
              {t("Explore", "Explorer", "Explorar")}
            </h4>
            <ul className="space-y-2.5">
              {footerLinks.explore.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-accent transition-colors no-underline"
                  >
                    {t(link.labelEn, link.labelFr, link.labelEs)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Captain ROCA — #DyslexSEA
          </p>
          <p className="text-xs text-muted-foreground">
            {t(
              "Built for dyslexic minds. Made for the ocean.",
              "Conçu pour les esprits dyslexiques. Fait pour l'océan.",
              "Diseñado para mentes disléxicas. Hecho para el océano."
            )}
          </p>
        </div>
      </div>
    </footer>
  );
};
